import { useState } from 'react'
import { Button as AriaButton } from 'react-aria-components'
import * as v from 'valibot'
import Text from 'src/components/ui/Text'
import { useLocalizedStringPairGetter } from 'src/i18n/localizedContent'
import type { CollectionCardSetProgress } from 'src/services/apollo/generated/graphql'
import { cx } from 'src/utils/cx'
import type { CollectionCardSetDataType } from '../UserDecoration/schema/collectionCard'
import { CollectionCardSetDataSchema } from '../UserDecoration/schema/collectionCard'
import {
  getCollectionCardProgressString,
  getCoverCollectionCard,
} from '../UserDecoration/utils/collectionCard'
import completionStamp from './assets/completion-stamp.webp'
import { CollectionCardCoverBackground } from './CollectionCardItem'
import { CollectionCardSetDialog } from './CollectionCardSetModal'

export function CollectionCardSetWithProgressItem(props: {
  collectionCardSetProgress: CollectionCardSetProgress
  onRefetch?: () => void
}) {
  const [openModal, setOpenModal] = useState(false)
  const { ownedCollectionCards, collectionCardSet } =
    props.collectionCardSetProgress

  const coverCard = getCoverCollectionCard(ownedCollectionCards)

  const parsedData = v.safeParse(
    CollectionCardSetDataSchema,
    collectionCardSet?.data,
  )

  if (!parsedData.success) return null

  return (
    <>
      <AriaButton
        onPress={() => setOpenModal(true)}
        className="relative outline-none"
      >
        <CollectionCardCoverBackground collectionCard={coverCard} />
        <CollectionCardSetProgressOverlay
          collectionCardSetProgress={props.collectionCardSetProgress}
          className="absolute bottom-0 w-full"
          collectionCardSetData={parsedData.output}
        />
      </AriaButton>
      <CollectionCardSetDialog
        open={openModal}
        onClose={() => setOpenModal(false)}
        collectionCardSetProgress={props.collectionCardSetProgress}
        collectionCardSetData={parsedData.output}
      />
    </>
  )
}

function CollectionCardSetProgressOverlay(
  props: {
    collectionCardSetProgress: CollectionCardSetProgress
    collectionCardSetData: CollectionCardSetDataType
  } & StyleProps,
) {
  const getText = useLocalizedStringPairGetter()
  const progressString = getCollectionCardProgressString(
    props.collectionCardSetProgress,
  )

  const { ownedCollectionCards, totalCollectionCards } =
    props.collectionCardSetProgress
  const isComplete =
    totalCollectionCards.length > 0 &&
    ownedCollectionCards.length === totalCollectionCards.length

  return (
    <div
      className={cx(
        props.className,
        'px-4 py-6 backdrop-blur-sm bg-neutral-100/70 dark:bg-neutral-800/70',
      )}
    >
      {/* Fixed at 2 lines to keep the overlay container a uniform height across all card sets — without this, sets with longer names grow the overlay and misalign the grid */}
      <h3 className="h-[3em] leading-normal flex items-center justify-center">
        <span className="line-clamp-2 text-center">
          {props.collectionCardSetData
            ? getText(props.collectionCardSetData.name)
            : 'Unknown Card Set'}
        </span>
      </h3>
      <span className="top-1 right-1 absolute text-xs">
        <Text
          i18nKey={'user:user-decoration.collection-card.card-set.progress'}
        />
        <Text i18nKey={'common:punctuations.colon'} /> {progressString}
      </span>
      {isComplete && (
        <img
          src={completionStamp}
          alt="100% completion stamp"
          className="absolute top-0 right-0 h-full p-2 aspect-square pointer-events-none opacity-50 rotate-[-12deg]"
        />
      )}
    </div>
  )
}
